/**
 * **El registro de errores de la pantalla** (0182): lo que se rompe en el navegador llega al ERP
 * (`POST /api/app-errors`) y el owner lo ve en Mejoras › Errores y en la campanita. Sin servicios
 * externos.
 *
 * Tres fuentes: `window.onerror`, las promesas sin atender (`unhandledrejection`) y la página de error
 * de las rutas (`RouteErrorPage`, la que se ve cuando una pantalla no carga). **Se manda solo la ruta,
 * sin la consulta**, el tipo, el mensaje y la pila: nunca el contenido de la pantalla, las cookies ni lo
 * escrito. Lo que el servidor rechazó a propósito (un 4xx: «falta permiso», «revisa la cantidad») no es
 * un error del ERP y no se manda; un 5xx ya lo anotó el servidor. Hasta 20 por carga de página y uno
 * igual por minuto. Si mandar falla, se calla: el registro nunca causa otro error.
 */

import { ApiError } from "@/lib/api";

type Tipo = "error" | "promise" | "screen";

const TOPE = 20;
const vistos = new Map<string, number>();
let enviados = 0;

function texto(valor: unknown): { message: string; detail?: string } {
  if (valor instanceof Error) return { message: `${valor.name}: ${valor.message}`.slice(0, 2000), detail: valor.stack?.slice(0, 20000) };
  return { message: String(valor ?? "Error sin mensaje").slice(0, 2000) };
}

/** ¿Vale la pena anotarlo? No los rechazos esperados del servidor ni lo que ya anotó él. */
function cuenta(valor: unknown): boolean {
  if (valor instanceof ApiError) return false;
  return !(valor instanceof Error && valor.name === "AbortError");
}

export function avisarError(tipo: Tipo, valor: unknown): void {
  if (!cuenta(valor) || enviados >= TOPE) return;
  const { message, detail } = texto(valor);
  const route = window.location.pathname;
  const llave = `${tipo}|${route}|${message}`;
  const ahora = Date.now();
  if ((vistos.get(llave) ?? 0) > ahora - 60_000) return;
  vistos.set(llave, ahora);
  enviados += 1;
  void fetch("/api/app-errors", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ route, kind: tipo, message, detail }),
    keepalive: true,
  }).catch(() => undefined);
}

let instalado = false;

/** Se llama una vez al arrancar (`main.tsx`). */
export function instalarRegistroDeErrores(): void {
  if (instalado) return;
  instalado = true;
  window.addEventListener("error", (e) => avisarError("error", e.error ?? e.message));
  window.addEventListener("unhandledrejection", (e) => avisarError("promise", e.reason));
}

/** Solo para las pruebas: vuelve a contar desde cero. */
export function reiniciarRegistroDeErrores(): void {
  vistos.clear();
  enviados = 0;
}
